import * as yup from "yup";
import React, { useEffect, useState } from "react";

import { Grid, MenuItem, TextField, Typography } from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";

import useAxios from "../../../../hooks/useAxios";
import useAlert from "../../../../hooks/useAlert";
import {
  __createTmobilejMessageFilter,
  __deleteTmobilejMessageFilter,
  __updateTmobilejMessageFilter,
} from "../../../../api/sim";

const lineValidationSchema = yup.object({
  number: yup
    .string("number")
    .min(10, "Should be exactly 10 digits long")
    .max(10, "Should be exactly 10 digits long")
    .required(),
});

const validateNumbersText = (data) => {
  const errors = {};
  if (!data) return { errors, numbers: [] };

  const numbers = [];
  const lines = data.split("\n");
  lines.some((line, idx) => {
    const trimmed = String(line || "").trim();
    if (!trimmed) return false; // allow empty lines

    const fields = trimmed.split(",");
    if (fields.length < 1) {
      errors.lines = `Invalid format in line ${idx + 1}: ${line}`;
      return true;
    }

    try {
      const obtainedData = { number: fields[0].trim() };
      lineValidationSchema.validateSync(obtainedData);
      if (isNaN(obtainedData.number)) throw new Error("Invalid Number");

      numbers.push(obtainedData.number);
      if (numbers.indexOf(obtainedData.number) !== numbers.length - 1) {
        errors.lines = `Duplicate number value in line ${idx + 1}`;
        return true;
      }
    } catch (err) {
      errors.lines = `Invalid format in line ${idx + 1}: ${line}`;
      return true;
    }

    return false;
  });

  return { errors, numbers };
};

const fromE164To10Digits = (value) => {
  const s = String(value || "").trim();
  if (/^\+1\d+$/.test(s)) return s.slice(2);
  if (/^\d{11}$/.test(s) && s.startsWith("1")) return s.slice(1);
  if (/^\d{10}$/.test(s)) return s;
  return "";
};

const stringify10DigitList = (nums) => {
  if (!Array.isArray(nums) || nums.length === 0) return "";
  return nums.map((n) => fromE164To10Digits(n)).filter(Boolean).join("\n");
};

const pickMessageFilterId = (filter) =>
  filter?.FilterId || filter?.MessageFilterId || filter?.filterId || filter?.id || null;

const pickMessageFilterMode = (filter) =>
  filter?.FilterMode || filter?.filterMode || filter?.mode || "WHITELIST";

const pickAllowedContacts = (filter) =>
  filter?.AllowedContacts || filter?.allowedContacts || filter?.AllowedContact || [];

const pickBlockedContacts = (filter) =>
  filter?.BlockedContacts || filter?.blockedContacts || filter?.BlockedContact || [];

const MessageFilters = ({ phone, phoneError, validatePhone, initialData, onDataUpdate }) => {
  const { setAlertMessage } = useAlert();

  const [messageFilterId, setMessageFilterId] = useState(null);
  const [messageSubscriberId, setMessageSubscriberId] = useState(null);
  const [mode, setMode] = useState("WHITELIST");
  const [allowedContactsText, setAllowedContactsText] = useState("");
  const [blockedContactsText, setBlockedContactsText] = useState("");
  const [allowedContacts, setAllowedContacts] = useState([]);
  const [blockedContacts, setBlockedContacts] = useState([]);
  const [allowedContactsError, setAllowedContactsError] = useState("");
  const [blockedContactsError, setBlockedContactsError] = useState("");
  const [applyToOutbound, setApplyToOutbound] = useState(null);
  const [applyToInbound, setApplyToInbound] = useState(null);
  const [blockUnknownNumbers, setBlockUnknownNumbers] = useState(null);
  const [blockLinks, setBlockLinks] = useState(null);
  const [blockMedia, setBlockMedia] = useState(null);

  const validateMessageLists = () => {
    if (mode === "WHITELIST") {
      const { errors, numbers } = validateNumbersText(allowedContactsText);
      setAllowedContactsError(errors.lines || "");
      setAllowedContacts(errors.lines ? [] : numbers);
      return !errors.lines;
    }
    const { errors, numbers } = validateNumbersText(blockedContactsText);
    setBlockedContactsError(errors.lines || "");
    setBlockedContacts(errors.lines ? [] : numbers);
    return !errors.lines;
  };
  const [createMsgResp, createMsgCall, createMsgLoading] = useAxios(
    __createTmobilejMessageFilter(phone)
  );
  const [updateMsgResp, updateMsgCall, updateMsgLoading] = useAxios(
    __updateTmobilejMessageFilter(phone, messageFilterId || "")
  );
  const [deleteMsgResp, deleteMsgCall, deleteMsgLoading] = useAxios(
    __deleteTmobilejMessageFilter(messageFilterId || "")
  );

  const hydrateMessageFromData = (data) => {
    const rawFilter = data?.filter || null;

    // Handle both array and object responses
    let f = null;
    if (Array.isArray(rawFilter) && rawFilter.length > 0) {
      f = rawFilter[0];
    } else if (rawFilter && typeof rawFilter === "object") {
      f = rawFilter;
    }

    if (!f) {
      // No filter found, reset to empty state
      setMessageFilterId(null);
      setMessageSubscriberId(data?.subscriberId || null);
      setMode("WHITELIST");
      setAllowedContactsText("");
      setBlockedContactsText("");
      setAllowedContacts([]);
      setBlockedContacts([]);
      setAllowedContactsError("");
      setBlockedContactsError("");
      return;
    }

    setMessageFilterId(pickMessageFilterId(f));
    setMessageSubscriberId(
      data?.subscriberId || f?.SubscriberId || f?.subscriberId || null
    );
    setMode(pickMessageFilterMode(f));

    const allowedContactsList = pickAllowedContacts(f);
    const blockedContactsList = pickBlockedContacts(f);
    const allowedStr = stringify10DigitList(allowedContactsList);
    const blockedStr = stringify10DigitList(blockedContactsList);

    setAllowedContactsText(allowedStr);
    setBlockedContactsText(blockedStr);

    const allowedParsed = validateNumbersText(allowedStr);
    setAllowedContactsError(allowedParsed.errors.lines || "");
    setAllowedContacts(allowedParsed.errors.lines ? [] : allowedParsed.numbers);

    const blockedParsed = validateNumbersText(blockedStr);
    setBlockedContactsError(blockedParsed.errors.lines || "");
    setBlockedContacts(blockedParsed.errors.lines ? [] : blockedParsed.numbers);

    // Extract view-only fields
    setApplyToOutbound(f?.ApplyToOutbound ?? null);
    setApplyToInbound(f?.ApplyToInbound ?? null);
    setBlockUnknownNumbers(f?.BlockUnknownNumbers ?? null);
    setBlockLinks(f?.BlockLinks ?? null);
    setBlockMedia(f?.BlockMedia ?? null);
  };

  // Hydrate form when initialData changes
  useEffect(() => {
    if (initialData) {
      hydrateMessageFromData(initialData);
    }
  }, [initialData]);

  useEffect(() => {
    if (!createMsgResp?.status) return;
    setAlertMessage({ type: "success", message: "Message filter created" });
    if (createMsgResp.data && onDataUpdate) {
      onDataUpdate(createMsgResp.data);
    }
    hydrateMessageFromData(createMsgResp.data);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createMsgResp]);

  useEffect(() => {
    if (!updateMsgResp?.status) return;
    setAlertMessage({ type: "success", message: "Message filter updated" });
    if (updateMsgResp.data && onDataUpdate) {
      onDataUpdate(updateMsgResp.data);
    }
    hydrateMessageFromData(updateMsgResp.data);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateMsgResp]);

  useEffect(() => {
    if (!deleteMsgResp?.status) return;
    setAlertMessage({ type: "success", message: "Message filter deleted" });
    setMessageFilterId(null);
    setMessageSubscriberId(null);
    setMode("WHITELIST");
    setAllowedContactsText("");
    setBlockedContactsText("");
    setAllowedContacts([]);
    setBlockedContacts([]);
    setAllowedContactsError("");
    setBlockedContactsError("");
    if (onDataUpdate) {
      onDataUpdate({ filter: null, subscriberId: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleteMsgResp]);

  const buildMessagePayload = () => {
    const payload = { FilterMode: mode };
    if (mode === "WHITELIST") {
      payload.AllowedContacts = allowedContacts;
      payload.BlockedContacts = [];
    } else {
      payload.AllowedContacts = [];
      payload.BlockedContacts = blockedContacts;
    }
    return payload;
  };

  const handleCreateMessage = async () => {
    try {
      const err = validatePhone(phone);
      if (err) throw new Error(err);
      if (!validateMessageLists()) throw new Error("Fix invalid numbers");
      await createMsgCall({ data: buildMessagePayload() });
    } catch (e) {
      setAlertMessage({ type: "error", message: e.message || "Invalid input" });
    }
  };

  const handleUpdateMessage = async () => {
    if (!messageFilterId) return;
    try {
      const err = validatePhone(phone);
      if (err) throw new Error(err);
      if (!validateMessageLists()) throw new Error("Fix invalid numbers");
      await updateMsgCall({ data: buildMessagePayload() });
    } catch (e) {
      setAlertMessage({ type: "error", message: e.message || "Invalid input" });
    }
  };

  const handleDeleteMessage = async () => {
    if (!messageFilterId) return;
    await deleteMsgCall();
  };

  const loading = createMsgLoading || updateMsgLoading || deleteMsgLoading;
  const hasMessageFilter = Boolean(messageFilterId);
  const hasMessageErrors =
    Boolean(phoneError) ||
    (mode === "WHITELIST" ? Boolean(allowedContactsError) : Boolean(blockedContactsError));

  return (
    <>
      <Grid item>
        <Typography variant="body2">
          SubscriberId: {messageSubscriberId || "—"} | FilterId:{" "}
          {messageFilterId || "—"}
        </Typography>
      </Grid>

      {!hasMessageFilter && (
        <Grid item>
          <Typography variant="subtitle2">
            No message filters available.
          </Typography>
        </Grid>
      )}

      {hasMessageFilter && (
        <Grid item>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Filter Settings (View Only):
          </Typography>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            ApplyToOutbound: {String(applyToOutbound ?? "—")} | ApplyToInbound: {String(applyToInbound ?? "—")}
          </Typography>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            BlockUnknownNumbers: {String(blockUnknownNumbers ?? "—")} | BlockLinks: {String(blockLinks ?? "—")}
          </Typography>
          <Typography variant="body2">
            BlockMedia: {String(blockMedia ?? "—")}
          </Typography>
        </Grid>
      )}

      <>
        <Grid item>
          <TextField
            select
            fullWidth
            label="Filter Mode"
            value={mode}
            onChange={(e) => {
              const next = e.target.value;
              setMode(next);
              if (next === "WHITELIST") {
                const parsed = validateNumbersText(allowedContactsText);
                setAllowedContactsError(parsed.errors.lines || "");
                setAllowedContacts(parsed.errors.lines ? [] : parsed.numbers);
              } else {
                const parsed = validateNumbersText(blockedContactsText);
                setBlockedContactsError(parsed.errors.lines || "");
                setBlockedContacts(parsed.errors.lines ? [] : parsed.numbers);
              }
            }}
          >
            <MenuItem value="WHITELIST">WHITELIST</MenuItem>
            <MenuItem value="BLACKLIST">BLACKLIST</MenuItem>
          </TextField>
        </Grid>

        {mode === "WHITELIST" ? (
          <Grid item>
            <TextField
              fullWidth
              multiline
              minRows={4}
              label="Allowed Contacts (10 digits, one per line)"
              value={allowedContactsText}
              error={Boolean(allowedContactsError)}
              helperText={allowedContactsError || "Digits only; one per line"}
              onChange={(e) => {
                const text = e.target.value;
                setAllowedContactsText(text);
                const parsed = validateNumbersText(text);
                setAllowedContactsError(parsed.errors.lines || "");
                setAllowedContacts(parsed.errors.lines ? [] : parsed.numbers);
              }}
            />
          </Grid>
        ) : (
          <Grid item>
            <TextField
              fullWidth
              multiline
              minRows={4}
              label="Blocked Contacts (10 digits, one per line)"
              value={blockedContactsText}
              error={Boolean(blockedContactsError)}
              helperText={blockedContactsError || "Digits only; one per line"}
              onChange={(e) => {
                const text = e.target.value;
                setBlockedContactsText(text);
                const parsed = validateNumbersText(text);
                setBlockedContactsError(parsed.errors.lines || "");
                setBlockedContacts(parsed.errors.lines ? [] : parsed.numbers);
              }}
            />
          </Grid>
        )}

          <Grid item sx={{ display: "flex", gap: 1 }}>
            {!hasMessageFilter ? (
              <LoadingButton
                loading={createMsgLoading}
                disabled={loading || hasMessageErrors}
                variant="contained"
                onClick={handleCreateMessage}
              >
                Create Message Filter
              </LoadingButton>
            ) : (
              <>
                <LoadingButton
                  loading={updateMsgLoading}
                  disabled={loading || hasMessageErrors}
                  variant="contained"
                  onClick={handleUpdateMessage}
                >
                  Update Message Filter
                </LoadingButton>
                <LoadingButton
                  loading={deleteMsgLoading}
                  disabled={loading}
                  color="error"
                  variant="outlined"
                  onClick={handleDeleteMessage}
                >
                  Delete Message Filter
                </LoadingButton>
              </>
            )}
          </Grid>
      </>
    </>
  );
};

export default MessageFilters;

